import React, { useEffect, useRef, useState } from 'react';
import DatePicker, { DateObject, DatePickerRef } from 'react-multi-date-picker';
import 'react-multi-date-picker/styles/colors/red.css';
import Icon from 'react-multi-date-picker/components/icon';
import { dayjs } from '@umanforge/common';
import Button from '@common_components/buttons/button.component';

interface DatePickerSoloInputProp {
  label?: string;
  placeholder?: string;
  labelPosition?: 'top' | 'left';
  textSize?: string;
  minDate?: Date;
  maxDate?: Date;
  startDate?: Date | null;
  defaultValue?: Date | null;
  disable?: boolean;
  endDate?: Date | null;
  clearAfrerClose?: boolean;
  shadow?: string;
  border?: string;
  width?: string;
  inputClassName?: string;
  hideIcon?: boolean;
  compact?: boolean;
  customButtons?: { label: string, date: dayjs.Dayjs | null }[]
  onChange?: ( date: Date | null ) => void;
  onClose?: ( date: Date ) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const DatePickerSoloInput: React.FC<DatePickerSoloInputProp> = ( {
  label,
  customButtons,
  placeholder,
  shadow = 'shadow',
  labelPosition = 'left',
  border,
  disable,
  textSize = 'w-3/12 mr-4',
  width = 'w-full',
  minDate,
  maxDate,
  defaultValue,
  clearAfrerClose,
  inputClassName,
  hideIcon = false,
  compact = false,
  onChange,
  onClose,
  onFocus,
  onBlur
} ) => {
  const localDayjs = dayjs.Ls[ dayjs.locale() ];
  const pickerRef = useRef();

  const [ date, setDate ] = useState<Date | null>( defaultValue ?? null );
  const [ max, setMax ] = useState<dayjs.Dayjs | undefined>( maxDate ? dayjs( maxDate ).startOf( 'day' ) : undefined );
  const [ min, setMin ] = useState<dayjs.Dayjs | undefined>( minDate ? dayjs( minDate ).startOf( 'day' ) : undefined );
  const [ isOpen, setIsOpen ] = useState( false );

  useEffect( () => {
    if ( !isOpen ) {
      if ( onClose && date ) {
        onClose( date );
      }
      if ( clearAfrerClose ) {
        setDate( null );
      }
    }
  }, [ isOpen ] );

  useEffect( () => {
    setDate( defaultValue ?? null );
  }, [ defaultValue ] );

  useEffect( () => {
    if ( minDate ) {
      setMin( dayjs( minDate ).startOf( 'day' ) );
    } else {
      setMin( undefined );
    }
  }, [ minDate ] );

  useEffect( () => {
    if ( maxDate ) {
      setMax( dayjs( maxDate ).startOf( 'day' ) );
    } else {
      setMax( undefined );
    }
  }, [ maxDate ] );

  const handleChange = ( dateObjects: DateObject | null ) => {
    setDate( dateObjects?.toDate() ?? null );
    if ( onChange ) {
      onChange( dateObjects?.toDate() ?? null );
    }
  };

  const baseShellClassName = 'flex w-full h-[32px] m-0 items-center rounded-md overflow-hidden';

  const shellClassName = compact
    ? `${baseShellClassName} bg-transparent`
    : `${baseShellClassName} border border-solid outline-0
      ${shadow}
      ${border ? border : ( disable ? 'border-gray-base-veryLight' : 'border-white hover:border-black' )}
      ${!border && isOpen && !disable ? 'border-black' : ''}
      ${disable ? 'bg-gray-base-veryLight border-gray-base-veryLight cursor-not-allowed' : 'bg-white cursor-pointer'}`;

  const inputClassValue = compact
    ? `flex w-full h-full px-2 py-0 border-0 outline-0 items-center truncate rounded-md ${ inputClassName ?? '' } ${disable ? 'bg-transparent cursor-not-allowed text-gray-400' : 'bg-transparent'}`
    : `flex w-full h-full px-2 py-0 border-0 outline-0 items-center rounded-md ${ inputClassName ?? '' } ${disable ? 'bg-gray-base-veryLight cursor-not-allowed text-gray-400' : 'bg-white'}`;

  return (
    <div className={`flex h-full ${width} max-w-full ${
      labelPosition === 'left' ? '' : 'flex-col'
    }`}>
      {/* Label */}
      {label && (
        <label
          className={`text-gray-600 ${textSize} ${
            labelPosition === 'left' ? 'flex items-center' : ''
          }`}
        >
          {label}
        </label>
      )}
      {/* Input */}
      <div className={shellClassName}>
        <DatePicker
          disabled={disable}
          ref={pickerRef}
          weekStartDayIndex={localDayjs.weekStart}
          weekDays={localDayjs.weekdaysShort}
          months={localDayjs.months}
          format={localDayjs.formats.L}
          zIndex={10000}
          portal
          minDate={min?.toDate()}
          maxDate={max?.toDate()}
          placeholder={placeholder}
          value={date}
          editable={false}
          onChange={handleChange}
          onOpen={() => {
            if ( onFocus ) {
              onFocus();
            }
            setIsOpen( true );
          } }
          onClose={() => {
            if ( onBlur ) {
              onBlur();
            }
            setIsOpen( false );
          } }
          showOtherDays
          className={`w-full h-full ${disable ? 'rmdp-disabled' : ''}`}
          inputClass={inputClassValue}
          style={{ width: '100%', height: '100%' }}
        >
          {
            customButtons && customButtons.length > 0 && (
              <div className='flex flex-wrap h-fit w-[248px] gap-2 p-2 justify-center border-t border-gray-100 mx-2'>
                {
                  customButtons.map( button => {
                    const isAlreadySelected = dayjs( button.date ).isSame( dayjs( date ), 'day' ) || ( !dayjs( button.date ).isValid() && !dayjs( date ).isValid() );
                    return (
                      <div
                        key={ button.label + button.date?.unix() }
                        className='flex justify-center w-full'
                      >
                        <Button
                          label={ button.label }
                          width='w-[248px]'
                          height='h-4'
                          padding='px-3 py-3'
                          color='rmdp-input-custom-button'
                          textSize='text-[14px]'
                          textColor='text-gray-400'
                          disable={ isAlreadySelected }
                          onClick={() => {
                            if ( !isAlreadySelected ) {
                              handleChange( button.date ? new DateObject( button.date.toDate() ) : null );
                              const ref = ( pickerRef.current as unknown as DatePickerRef | null );
                              if ( ref ) {
                                ref.closeCalendar();
                              }
                            }
                          } }
                        />
                      </div>
                    );
                  } )
                }
              </div>
            )
          }
        </DatePicker>
        {!hideIcon && !disable && (
          <div
            className="px-2 flex items-center justify-center flex-shrink-0 cursor-pointer"
            onClick={() => {
              const ref = ( pickerRef.current as unknown as DatePickerRef | null );
              if ( ref ) {
                if ( isOpen ) {
                  ref.closeCalendar();
                } else {
                  ref.openCalendar();
                }
              }
            }}
          >
            <Icon className='w-5 h-5 flex-shrink-0'/>
          </div>
        )}
        {!hideIcon && disable && (
          <div className="px-2 flex items-center justify-center flex-shrink-0 cursor-not-allowed">
            <Icon className='w-5 h-5 flex-shrink-0 text-gray-400'/>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatePickerSoloInput;
